/**
 * @file BookOverInvoiceFlow.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description "Book over…" on an invoice already raised (Cam, 3 Nov 2026 —
 *              "split one invoice across dates or move it after it has been
 *              invoiced and record the book over on the order as we go. This
 *              needs to be possible from variation report modal, search
 *              invoices table context menu", "so long as the invoice isn't
 *              paid"). One flow for every place it is offered: Search Invoices'
 *              row menu and right-click, the invoice window, Invoice Detail's
 *              variation review, Pricing & Costing's invoiced rows.
 *
 *   1. InvoiceBookOverDialog with `invoiceId` — the dates, note, split.
 *   2. POST /api/invoices/:id/book-over (services/invoiceBookOverService);
 *      the prior-month question once (usePriorMonthConfirm); a refusal shown
 *      in the window (nothing changed).
 *   3. Done: what happened (and anything still to do — a part Xero would not
 *      void, an invoice not sent to Xero yet). When the old invoice had gone
 *      out to the customer: offer to send the revised and new invoice(s) to
 *      whoever had it (POST /api/invoices/:id/send-email — the existing send;
 *      `revised` on the one changed in place). Never sent by itself.
 *
 *   Props: { invoiceId, onClose(), onDone(result) } — onDone after a booking
 *   (the caller re-reads its rows), onClose whatever happened.
 */

import React, { useState } from 'react';
import InvoiceBookOverDialog from './InvoiceBookOverDialog';
import ConfirmDialog from './ConfirmDialog';
import AlertDialog from './AlertDialog';
import usePriorMonthConfirm from '../hooks/usePriorMonthConfirm';
import { API_URL } from '../config';

/** The send offer's question (pure). */
export function sendOfferMessage(offer) {
  if (!offer) return '';
  const nos = (offer.invoices || []).map(i => i.invoice_no).join(', ');
  const who = (offer.to || []).length ? offer.to.join(', ') : null;
  return `${(offer.gone_out || []).join('; ')}. `
    + (who
      ? `Send ${nos} to ${who} now? The one changed in place says "Revised".`
      : `There is no email address it went to — send ${nos} to the customer from Search Invoices.`);
}

function BookOverInvoiceFlow({ invoiceId, onClose, onDone }) {
  const priorMonth = usePriorMonthConfirm();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [offer, setOffer] = useState(null);
  const [sendNote, setSendNote] = useState(null);

  const confirm = async (bookOver, { expected } = {}) => {
    setBusy(true);
    setError(null);
    try {
      const out = await priorMonth.run((extra = {}) => fetch(`${API_URL}/api/invoices/${invoiceId}/book-over`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ bookOver, expected, ...extra })
      }));
      if (!out || out.declined) return;
      const { res, data } = out;
      if (!res.ok || !data || !data.success) {
        setError((data && data.error) || 'It could not be booked over — nothing was changed');
        return;
      }
      setResult(data);
      if (onDone) onDone(data);
      if (data.send_offer && (data.send_offer.invoices || []).length) setOffer(data.send_offer);
    } catch (err) {
      setError(`It could not be booked over: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const o = offer;
    setOffer(null);
    if (!o || !(o.to || []).length) { setSendNote(sendOfferMessage(o)); return; }
    const sent = []; const failed = [];
    for (const inv of o.invoices) {
      try {
        const res = await fetch(`${API_URL}/api/invoices/${inv.id}/send-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ to: o.to.join(', '), revised: !!inv.revised })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) throw new Error(data.error || 'not sent');
        sent.push(inv.invoice_no);
      } catch (err) {
        failed.push(`${inv.invoice_no} (${err.message})`);
      }
    }
    setSendNote(`${sent.length ? `Sent ${sent.join(', ')} to ${o.to.join(', ')}.` : ''}${failed.length ? ` Not sent: ${failed.join('; ')} — send ${failed.length === 1 ? 'it' : 'them'} from Search Invoices.` : ''}`.trim());
  };

  if (result) {
    const parts = [result.message, result.xero_warning ? `Xero: ${result.xero_warning}` : null, sendNote].filter(Boolean);
    return (
      <>
        <ConfirmDialog
          isOpen={!!offer}
          type="info"
          title="Send the customer the new invoice(s)?"
          message={sendOfferMessage(offer)}
          confirmText={(offer?.to || []).length ? 'Send them' : 'OK'}
          cancelText="Not now"
          onConfirm={send}
          onCancel={() => setOffer(null)}
        />
        {!offer && (
          <AlertDialog
            isOpen
            type={(result.still_live || []).length ? 'warning' : 'success'}
            title="Booked over"
            message={parts.join('\n\n')}
            onClose={onClose}
          />
        )}
      </>
    );
  }

  return (
    <>
      <InvoiceBookOverDialog
        invoiceId={invoiceId}
        busy={busy}
        error={error}
        onCancel={onClose}
        onConfirm={confirm}
      />
      {priorMonth.dialog}
    </>
  );
}

export default BookOverInvoiceFlow;
